import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const navigate = useNavigate();
  const handleConsultationClick = () => {
    navigate('/contactus');
  };
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.1 // Trigger when 10% of the component is visible
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }


    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '2rem',
      boxSizing: 'border-box',
      position: 'relative',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
      transition: 'opacity 1s ease, transform 1s ease',
      '@media (max-width: 768px)': {
        padding: '1rem',
      },
      '@media (max-width: 480px)': {
        padding: '0.5rem',
      }
    },
    headingContainer: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      marginBottom: '2rem',
      position: 'relative',
      zIndex: 10,
      paddingTop: '1rem',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
      transition: 'opacity 1s ease 0.3s, transform 1s ease 0.3s',
      '@media (max-width: 768px)': {
        paddingTop: '0.5rem',
      }
    },
    heading: {
      fontSize: 'clamp(2rem, 5vw, 3rem)',
      fontWeight: 'bold',
      color: '#400684',
      fontFamily: 'Oswald, sans-serif',
      textTransform: 'uppercase',
      textAlign: 'center',
      marginBottom: '1rem',
      '@media (max-width: 1024px)': {
        fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
      },
      '@media (max-width: 768px)': {
        fontSize: 'clamp(1.6rem, 3vw, 2rem)',
      },
      '@media (max-width: 480px)': {
        fontSize: 'clamp(1.4rem, 2.5vw, 1.8rem)',
      }
    },
    contentWrapper: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      maxWidth: '1200px',
      width: '100%',
      gap: '2rem',
      '@media (max-width: 1024px)': {
        flexDirection: 'column',
        gap: '1rem',
      }
    },
    leftContent: {
      flex: '1',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start', // Left-aligned
      textAlign: 'left', // Left-aligned text
      padding: '1rem',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateX(0)' : 'translateX(-50px)',
      transition: 'opacity 1s ease 0.6s, transform 1s ease 0.6s',
      '@media (max-width: 768px)': {
        padding: '0.5rem',
        alignItems: 'center', // Center on smaller screens
        textAlign: 'center', // Center text on smaller screens
      }
    },
    rightContent: {
      flex: '1',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '1rem',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateX(0)' : 'translateX(50px)',
      transition: 'opacity 1s ease 0.6s, transform 1s ease 0.6s',
      '@media (max-width: 768px)': {
        padding: '0.5rem',
      }
    },
    subHeading: {
      fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: '600',
      '@media (max-width: 1024px)': {
        fontSize: 'clamp(1.1rem, 2.5vw, 1.3rem)',
      },
      '@media (max-width: 768px)': {
        fontSize: 'clamp(1rem, 2vw, 1.2rem)',
        textAlign: 'center',
      },
      '@media (max-width: 480px)': {
        fontSize: 'clamp(0.9rem, 1.8vw, 1.1rem)',
      }
    },
    paragraph: {
      fontSize: 'clamp(0.9rem, 2vw, 1rem)',
      color: '#666',
      maxWidth: '600px',
      marginBottom: '1.5rem',
      lineHeight: '1.6',
      '@media (max-width: 768px)': {
        fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)',
        maxWidth: '100%',
        textAlign: 'center',
      }
    },
    featureList: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1rem',
      marginTop: '1.5rem',
      width: '100%',
      maxWidth: '500px',
      '@media (max-width: 768px)': {
        gridTemplateColumns: '1fr 1fr',
        justifyContent: 'center',
      },
      '@media (max-width: 480px)': {
        gridTemplateColumns: '1fr',
        gap: '0.75rem',
      }
    },
    featureItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.75rem',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      transition: 'transform 0.3s ease',
      color: 'black',
      '@media (max-width: 480px)': {
        padding: '0.5rem',
        justifyContent: 'center',
      }
    },
    ctaButton: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#400684',
      color: 'white',
      border: 'none',
      borderRadius: '5px',
      fontSize: 'clamp(0.9rem, 2vw, 1rem)',
      cursor: 'pointer',
      marginTop: '1.5rem',
      transition: 'background-color 0.3s ease',
      alignSelf: 'flex-start', // Left-aligned
      '@media (max-width: 768px)': {
        alignSelf: 'center', // Center on smaller screens
        padding: '0.6rem 1.2rem',
        fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)',
      }
    },
    heroImage: {
      maxWidth: '100%',
      height: 'auto',
      borderRadius: '10px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
      '@media (max-width: 768px)': {
        maxWidth: '90%',
      }
    }
  };

  // Function to apply styles with media queries
  const applyStyles = (baseStyles) => {
    const style = { ...baseStyles };
    delete style['@media (max-width: 1024px)'];
    delete style['@media (max-width: 768px)'];
    delete style['@media (max-width: 480px)'];
    return style;
  };

  // Function to generate media query styles
  const getMediaQueryStyles = () => `
    @media (max-width: 1024px) {
      .contentWrapper {
        flex-direction: column;
        gap: 1rem;
      }
    }
    @media (max-width: 768px) {
      .container {
        padding: 0.5rem;
      }
      .leftContent, .rightContent {
        padding: 0.5rem;
      }
      .leftContent {
        align-items: center;
        text-align: center;
      }
      .subHeading, .paragraph {
        text-align: center;
      }
      .ctaButton {
        align-self: center;
      }
      .heroImage {
        max-width: 90%;
      }
    }
    @media (max-width: 480px) {
      .container {
        padding: 0.25rem;
      }
      .featureList {
        grid-template-columns: 1fr;
        gap: 0.75rem;
      }
    }
  `;

  return (
    <>
      <style>{getMediaQueryStyles()}</style>
      <div ref={sectionRef} style={applyStyles(styles.container)} className="container">
        {/* Centered Heading Across Both Sections */}
        <div style={applyStyles(styles.headingContainer)} className="headingContainer">
          <h1 style={applyStyles(styles.heading)} className="heading">
            Technology Industry Consultants
          </h1>
        </div>

        <div style={applyStyles(styles.contentWrapper)} className="contentWrapper">
          <div style={applyStyles(styles.leftContent)} className="leftContent">
            <h2 style={applyStyles(styles.subHeading)} className="subHeading">
              Business Voice & SIP Trunk Solutions
            </h2>
            <p style={applyStyles(styles.paragraph)} className="paragraph">
              An SIP trunk is the virtual version of an analog phone line. An SIP provider 
              can connect as many channels as you need to your PBX using SIP trunks, 
              allowing you to make local, long-distance, and international calls over the Internet.
            </p>
            
            <div style={applyStyles(styles.featureList)} className="featureList">
              <div style={applyStyles(styles.featureItem)} className="featureItem">
                <span>📞</span>
                <span>Local Calls</span>
              </div>
              <div style={applyStyles(styles.featureItem)} className="featureItem">
                <span>🌍</span>
                <span>International Calls</span>
              </div>
              <div style={applyStyles(styles.featureItem)} className="featureItem">
                <span>💼</span>
                <span>Business PBX</span>
              </div>
              <div style={applyStyles(styles.featureItem)} className="featureItem">
                <span>🔄</span>
                <span>Virtual Solutions</span>
              </div>
            </div>

            <button style={applyStyles(styles.ctaButton)} className="ctaButton"   onClick={handleConsultationClick}>
              Get Started
            </button>
          </div>

          <div style={applyStyles(styles.rightContent)} className="rightContent">
            <img 
              src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d" 
              alt="Office Communication" 
              style={applyStyles(styles.heroImage)}
              className="heroImage"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default HeroSection;